import React, { useRef } from 'react';
import { StanButton, StripePaymentInput, StripePaymentInputRef } from '@/components';
import { useUser } from '@/queries/useUser';
import { stanNotify } from '@/lib/stanNotify';
import { stanAnalytics } from '@/lib/stanAnalytics';
import { usePaymentMethod } from '../../queries/usePaymentMethod';

interface UpdatePaymentMethodSectionProps {
    isMobileSubscription: boolean;
    customerId: string;
}

export const UpdatePaymentMethodSection: React.FC<UpdatePaymentMethodSectionProps> = ({
    isMobileSubscription,
    customerId,
}) => {
    const { user } = useUser();
    const paymentInputRef = useRef<StripePaymentInputRef>(null);
    const { mutateAsync: updatePaymentMethod, isPending: isUpdatingPaymentMethod } = usePaymentMethod();

    const handleUpdatePaymentMethod = async () => {
        if (!paymentInputRef.current || !customerId) return;

        try {
            const fullName = user?.full_name || '';
            const email = user?.email || '';

            // Create payment method with Stripe
            const paymentMethodId = await paymentInputRef.current.createPaymentMethod(customerId, fullName, email);

            if (!paymentMethodId) {
                throw new Error('Failed to create payment method');
            }

            // Update payment method via API
            const result = await updatePaymentMethod({ paymentMethodId, customerId });

            if (!result.acknowledged) {
                throw new Error('Payment method update was not acknowledged');
            }

            stanNotify({
                type: 'success',
                title: 'Success!',
                text: 'Your payment method has been updated.',
            });

            // Track analytics
            stanAnalytics('platform-subscription-update-payment-method', {
                meta: {
                    user_id: user?.user_id,
                    username: user?.username,
                },
            });
        } catch (error) {
            console.error('Error updating payment method:', error);
            const errorMessage =
                (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
                'Please try again later.';
            stanNotify({
                type: 'error',
                title: 'Something went wrong',
                text: errorMessage,
            });
        }
    };

    return (
        <div className='max-w-lg space-y-6'>
            <h4>Payment Details</h4>

            <div className='space-y-4'>
                {isMobileSubscription ? (
                    <div className='border-neutral-tertiary rounded-lg border p-4'>
                        <p className='text-caption-1-regular mb-2'>Payment Method</p>
                        <p className='text-gray text-caption-1-regular'>
                            You subscribed on the Stan Mobile App. You can manage your subscription in the app settings.
                        </p>
                    </div>
                ) : (
                    <div className='space-y-6'>
                        <StripePaymentInput ref={paymentInputRef} showWallets={false} />

                        <StanButton
                            name='save-payment-method-button'
                            size='sm'
                            disabled={isUpdatingPaymentMethod}
                            loading={isUpdatingPaymentMethod}
                            onClick={handleUpdatePaymentMethod}
                        >
                            Update
                        </StanButton>
                    </div>
                )}
            </div>
        </div>
    );
};
